import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  FormHelperText,
  IconButton,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

const FETCH_DEBOUNCE_MS = 250;
const MIN_QUERY_LENGTH = 2;
const ALLOWED_PRIMARY_TYPES = ["premise", "subpremise", "street_address", "establishment"];
const PLACE_DETAIL_FIELD_MASK = "addressComponents,location,formattedAddress";
const PLACES_API_BASE = "https://places.googleapis.com/v1";

/* ─────────────────────── REST helpers (Places API New) ──────────────────────── */

function getApiKey() {
  return process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
}

function makeSessionToken() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

async function fetchAutocomplete({ input, sessionToken, signal }) {
  const apiKey = getApiKey();
  if (!apiKey) throw new Error("REACT_APP_GOOGLE_MAPS_API_KEY is not set");
  const res = await fetch(`${PLACES_API_BASE}/places:autocomplete`, {
    method: "POST",
    signal,
    headers: {
      "Content-Type": "application/json",
      "X-Goog-Api-Key": apiKey,
    },
    body: JSON.stringify({
      input,
      sessionToken,
      includedRegionCodes: ["us"],
      includedPrimaryTypes: ALLOWED_PRIMARY_TYPES,
    }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`Autocomplete ${res.status}${body ? `: ${body}` : ""}`);
  }
  const data = await res.json();
  return Array.isArray(data?.suggestions) ? data.suggestions : [];
}

async function fetchPlaceDetails({ placeId, sessionToken, signal }) {
  const apiKey = getApiKey();
  if (!apiKey) throw new Error("REACT_APP_GOOGLE_MAPS_API_KEY is not set");
  const url = new URL(`${PLACES_API_BASE}/places/${encodeURIComponent(placeId)}`);
  if (sessionToken) url.searchParams.set("sessionToken", sessionToken);
  const res = await fetch(url.toString(), {
    method: "GET",
    signal,
    headers: {
      "X-Goog-Api-Key": apiKey,
      "X-Goog-FieldMask": PLACE_DETAIL_FIELD_MASK,
    },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`Place details ${res.status}${body ? `: ${body}` : ""}`);
  }
  return await res.json();
}

/* ───────────────────── Prediction / place parsing helpers ───────────────────── */

function predictionMain(p) {
  return p?.structuredFormat?.mainText?.text || p?.text?.text || "";
}

function predictionSecondary(p) {
  return p?.structuredFormat?.secondaryText?.text || "";
}

function componentText(c, preferLong = true) {
  if (!c) return "";
  return (preferLong ? c.longText : c.shortText) || c.longText || c.shortText || "";
}

function placeToAddress(place) {
  const components = place?.addressComponents || [];

  let streetNumber = "";
  let route = "";
  let neighborhood = "";
  let sublocality = "";
  let locality = "";
  let state = "";
  let zip = "";
  for (const component of components) {
    const types = component.types || [];
    for (const t of types) {
      switch (t) {
        case "street_number":
          streetNumber = componentText(component);
          break;
        case "route":
          route = componentText(component, false);
          break;
        case "postal_code":
          zip = `${componentText(component)}${zip}`;
          break;
        case "locality":
          locality = componentText(component, false);
          break;
        case "sublocality_level_1":
          sublocality = componentText(component, false);
          break;
        case "neighborhood":
          neighborhood = componentText(component, false);
          break;
        case "administrative_area_level_1":
          state = componentText(component, false);
          break;
        default:
          break;
      }
    }
  }
  const street = [streetNumber, route].filter(Boolean).join(" ");
  const city = locality || sublocality || neighborhood;
  return { street, city, state, zip };
}

function placeToCoordinates(place) {
  const loc = place?.location;
  if (!loc) return null;
  if (loc.latitude == null || loc.longitude == null) return null;
  return { lat: loc.latitude, lng: loc.longitude };
}

/* ─────────────────────────────── Component ──────────────────────────────────── */

export default function AddressAutocomplete(props) {
  const { onPlaceSelect, value, required = false } = props;

  const [isOnline, setIsOnline] = useState(
    () => typeof navigator === "undefined" || navigator.onLine
  );
  const isOnlineRef = useRef(isOnline);
  isOnlineRef.current = isOnline;

  const [inputValue, setInputValue] = useState(value?.description || "");
  const [isSelected, setIsSelected] = useState(Boolean(value?.description));
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [hasRequestError, setHasRequestError] = useState(false);

  const sessionTokenRef = useRef(null);
  const abortRef = useRef(null);
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    const sync = () => {
      const on = typeof navigator !== "undefined" && navigator.onLine;
      isOnlineRef.current = on;
      setIsOnline(on);
    };
    window.addEventListener("online", sync);
    window.addEventListener("offline", sync);
    return () => {
      window.removeEventListener("online", sync);
      window.removeEventListener("offline", sync);
    };
  }, []);

  /** `value.description` / `value.unit` apply on first mount only. To reset from the parent,
   * remount with a different `key` or drive updates via `onPlaceSelect`. */

  const ensureSessionToken = useCallback(() => {
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = makeSessionToken();
    }
    return sessionTokenRef.current;
  }, []);

  const runFetch = useCallback(
    async (input) => {
      const tooShort = !input || input.length < MIN_QUERY_LENGTH;
      if (tooShort || !isOnlineRef.current) {
        setOptions([]);
        setLoading(false);
        return;
      }
      if (abortRef.current) abortRef.current.abort();
      const ac = new AbortController();
      abortRef.current = ac;

      setLoading(true);
      setHasRequestError(false);
      try {
        const suggestions = await fetchAutocomplete({
          input,
          sessionToken: ensureSessionToken(),
          signal: ac.signal,
        });
        if (ac.signal.aborted) return;
        setOptions(suggestions.map((s) => s.placePrediction).filter(Boolean));
      } catch (err) {
        if (err?.name === "AbortError") return;
        setOptions([]);
        setHasRequestError(true);
        if (typeof navigator === "undefined" || navigator.onLine) {
          console.error("[AddressAutocomplete] autocomplete failed", err);
        }
      } finally {
        if (abortRef.current === ac) {
          abortRef.current = null;
          setLoading(false);
        }
      }
    },
    [ensureSessionToken]
  );

  const resetField = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    setInputValue("");
    setIsSelected(false);
    setOptions([]);
    setLoading(false);
    setHasRequestError(false);
    onPlaceSelect({ description: "" });
  }, [onPlaceSelect]);

  useEffect(() => {
    if (!isOnline || !open) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      return;
    }
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      runFetch(inputValue);
    }, FETCH_DEBOUNCE_MS);
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [inputValue, isOnline, open, runFetch]);

  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const handleSelect = async (_e, picked) => {
    if (picked == null) return;
    if (typeof picked === "string") {
      onPlaceSelect({ ...(value || {}), description: picked });
      return;
    }
    const placeId = picked.placeId;
    if (!placeId) return;

    const previewLabel = predictionMain(picked);
    setIsSelected(true);
    if (previewLabel) setInputValue(previewLabel);

    try {
      const place = await fetchPlaceDetails({
        placeId,
        sessionToken: ensureSessionToken(),
      });
      const address = placeToAddress(place);
      const coordinates = placeToCoordinates(place);
      const description = place.formattedAddress || predictionMain(picked) || "";
      sessionTokenRef.current = null;
      setInputValue(description);
      onPlaceSelect({ ...address, coordinates, description });
    } catch (err) {
      console.error("[AddressAutocomplete] place details failed", err);
    }
  };

  const handleSearchClick = () => {
    if (!inputValue || inputValue.length < MIN_QUERY_LENGTH) return;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    setOpen(true);
    runFetch(inputValue);
  };

  const showOffline = !isOnline;
  const showRequestError = isOnline && hasRequestError && options.length === 0;
  const showSearchAdornment = !isSelected && inputValue.length > 0;
  const showNoMatches =
    isOnline &&
    !loading &&
    !hasRequestError &&
    !isSelected &&
    inputValue.length >= MIN_QUERY_LENGTH &&
    options.length === 0;

  return (
    <div className="autocomplete-container">
      <Box mb={2}>
        <Autocomplete
          freeSolo
          fullWidth
          autoComplete
          includeInputInList
          open={open}
          onOpen={() => setOpen(true)}
          onClose={(_e, reason) => {
            setOpen(false);
            if (reason === "blur" && !isSelected) resetField();
          }}
          options={options}
          loading={loading}
          filterOptions={(x) => x}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : predictionMain(option)
          }
          isOptionEqualToValue={(option, val) =>
            Boolean(option?.placeId && val?.placeId && option.placeId === val.placeId)
          }
          inputValue={inputValue}
          onInputChange={(_e, v, reason) => {
            if (reason === "reset") return;
            if (!v) {
              resetField();
              return;
            }
            setInputValue(v);
            setIsSelected(false);
            setOpen(true);
            setHasRequestError(false);
            if (v.length >= MIN_QUERY_LENGTH) {
              setLoading(true);
            } else {
              setLoading(false);
              setOptions([]);
            }
          }}
          onChange={handleSelect}
          loadingText={
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: "text.secondary",
              }}
            >
              <CircularProgress color="inherit" size={14} />
              Searching addresses…
            </Box>
          }
          renderInput={(params) => (
            <TextField
              {...params}
              required={required}
              name="street"
              variant="outlined"
              label="Address"
              inputProps={{ ...params.inputProps, autoComplete: "off" }}
              sx={(t) => ({
                "& .MuiOutlinedInput-root": { minHeight: t.spacing(7) },
              })}
              InputProps={{
                ...params.InputProps,
                endAdornment:
                  loading || showSearchAdornment ? (
                    <Box
                      className="MuiAutocomplete-endAdornment"
                      sx={{
                        position: "absolute",
                        right: 9,
                        top: "calc(50% - 14px)",
                      }}
                    >
                      {loading ? (
                        <CircularProgress color="inherit" size={18} />
                      ) : (
                        <IconButton aria-label="search" size="small" onClick={handleSearchClick}>
                          <SearchIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  ) : (
                    params.InputProps.endAdornment
                  ),
              }}
            />
          )}
          renderOption={(optionProps, option) => {
            const main = predictionMain(option);
            const secondary = predictionSecondary(option);
            return (
              <li {...optionProps} key={option.placeId || main}>
                <Box>
                  <Box sx={{ fontWeight: 500 }}>{main}</Box>
                  {secondary ? (
                    <Box sx={{ color: "text.secondary", fontSize: 12 }}>{secondary}</Box>
                  ) : null}
                </Box>
              </li>
            );
          }}
        />
        {showOffline && (
          <FormHelperText sx={{ mt: 0.5 }} error={true}>
            No internet. Please restore your data connection.
          </FormHelperText>
        )}
        {showRequestError && (
          <FormHelperText sx={{ mt: 0.5 }} error={true}>
            Unable to load suggestions. Please try again.
          </FormHelperText>
        )}
        {showNoMatches && (
          <FormHelperText sx={{ mt: 0.5 }}>No matching addresses found.</FormHelperText>
        )}
      </Box>

      <TextField
        id="unit"
        name="unit"
        variant="outlined"
        fullWidth
        label="Apt, suite or unit (optional)"
        value={value?.unit || ""}
        onChange={(e) => {
          onPlaceSelect({ ...value, unit: e.target.value });
        }}
      />
    </div>
  );
}
